import { makeContextFor } from "@me8eon/context";

export type TimeService = () => Date;
export type RelativeDayLabels = {
    today?: string;
    yesterday?: string;
};

export const dateTimeService: TimeService = () => new Date();

export const { use: useTime, Provider: TimeProvider } = makeContextFor("time", dateTimeService);

export function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export function normalizeLocaleForIntl(locale?: string): string | undefined {
    if (!locale) return locale;

    const normalized = locale.replace("_", "-").toLowerCase();
    if (normalized === "cz") return "cs";
    if (normalized === "cs-cs") return "cs-CZ";
    return locale;
}

/** When we get a number from the jwt token it often has baked into it a seconds since the epoch as an expiry and an issued at date
 * Here we return the milliseconds since the epoch in the time service time. We assume this is called roughly at the same moment as the token was issued
 * @param timeService We use this to get the current time. This is to help testing
 * @param issuedAt The issued at date from the token. This is in seconds since the epoch
 * @param expiry The expiry date from the token. This is in seconds since the epoch
 * @param float The 'float' is the time in seconds that we want to subtract from the expiry date. This is so that we get a new token before it expires
 */

export function calculateTokenRefreshTime(timeService: TimeService, issuedAt: number, expiry: number, float: number = 60): number {
    const secondsSinceEpoch = Math.floor(timeService().getTime() / 1000);
    const offset = secondsSinceEpoch - issuedAt; // this is the error between the time service and the token. We could make it a bit more precise but this is good enough
    return (expiry + offset - float) * 1000;

}

// time.service.ts

export const getFormattedDateTime = (
    date: Date = new Date(),
    format: string = "Y-m-d H:i:s",
    use12HourFormat: boolean = false,
    timeZone?: string,
): string => {
    const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: use12HourFormat,
        // Include weekday if the format string has "D"
        weekday: format.includes("D") ? "short" : undefined,
        timeZone: timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    // Use formatToParts for reliable extraction of date and time parts
    const parts = new Intl.DateTimeFormat("en-GB", options).formatToParts(date);
    const mapping = parts.reduce((acc, part) => {
        if (part.type !== "literal") {
            acc[part.type] = part.value;
        }
        return acc;
    }, {} as Record<string, string>);

    // For 12-hour formats, ensure the hour is correctly adjusted.
    let formattedHour = mapping.hour;
    if (use12HourFormat) {
        const hourNum = parseInt(mapping.hour, 10);
        formattedHour = ((hourNum % 12) || 12).toString().padStart(2, "0");
    }

    const { day, month, year, hour, minute, second, weekday, dayPeriod } = mapping;
    const monthName = new Intl.DateTimeFormat("en-GB", { month: "short" }).format(date);
    // Determine the output based on the requested format
    switch (format) {
        case "dd/MM/yyyy":
            return `${day}/${month}/${year}`;
        case "H:i":
            return `${mapping.hour}:${minute}${use12HourFormat ? " " + (dayPeriod || "") : ""}`.trim();
        case "Y-m-d":
            return `${year}-${month}-${day}`;
        case "Y-m-d H:i:s":
            return `${year}-${month}-${day} ${mapping.hour}:${minute}:${second}${use12HourFormat ? " " + (dayPeriod || "") : ""}`.trim();
        case "D m/d/Y h.i A":
            return `${weekday} ${month}/${day}/${year} ${formattedHour}.${minute} ${dayPeriod || ""}`.trim();
        case "d MMM Y  h:i A":
            return `${day} ${monthName} ${year}  ${formattedHour}:${minute} ${dayPeriod || ""}`.trim();
        case "D d MMM yyyy h.i A":
            return `${weekday} ${day} ${monthName} ${year} ${formattedHour}.${minute} ${dayPeriod || ""}`.trim();
        case "D d MMM yyyy | h.i A":
            return `${weekday} ${day} ${monthName} ${year} | ${formattedHour}.${minute} ${dayPeriod || ""}`.trim();
        default:
            return `${year}-${month}-${day} ${mapping.hour}:${minute}:${second}${use12HourFormat ? " " + (dayPeriod || "") : ""}`.trim();
    }
};


export function timeToTimeString(timeService: TimeService, d: Date, locale?: string, labels?: RelativeDayLabels): string {
    const now = timeService();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());

    if (target.getTime() === today.getTime()) {
        return labels?.today ?? "Today";
    }
    if (target.getTime() === yesterday.getTime()) {
        return labels?.yesterday ?? "Yesterday";
    }
    // Format as "5 April" or "5 April 2024" if year is not current year
    const day = target.getDate();
    const month = target.toLocaleString(normalizeLocaleForIntl(locale), { month: "long" });
    if (target.getFullYear() !== now.getFullYear()) {
        return `${day} ${month} ${target.getFullYear()}`;
    }
    return `${day} ${month}`;
}

// parse a custom date in the format like "20.02.2025 09:19:42"
export const parseCustomDate = (dateStr: string): Date => {
  const [datePart, timePart] = dateStr.split(' ');
  const [day, month, year] = datePart.split('.').map(Number);
  const [hours, minutes, seconds] = timePart.split(':').map(Number);

  return new Date(year, month - 1, day, hours, minutes, seconds);
}

export const isDateWithinGivenDays = (dateString: string, days: number = 30): boolean => {
  const inputDate = new Date(dateString);
  const now = new Date();

  // Create a date 'days' ago
  const pastDate = new Date();
  pastDate.setDate(now.getDate() - days);

  return inputDate >= pastDate && inputDate <= now;
}

/**
 * Formats a date and time in the format: dd/MM/yyyy HH:mm
 * @param date The date to format
 * @param timeZone Optional timezone for formatting
 * @returns Formatted string like "27/10/2026 - 14:29"
 */
export const formatDateTimeWithoutSeconds = (
    date: Date | string = new Date(),
    timeZone?: string,
): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    const parts = new Intl.DateTimeFormat("en-GB", options).formatToParts(dateObj);
    const mapping = parts.reduce((acc, part) => {
        if (part.type !== "literal") {
            acc[part.type] = part.value;
        }
        return acc;
    }, {} as Record<string, string>);

    return `${mapping.day}/${mapping.month}/${mapping.year} - ${mapping.hour}:${mapping.minute}`;
};
